import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AuthProvider as OAuthAuthProvider,
  useAuthContext as useOAuthAuthContext,
} from 'react-oauth2-code-pkce';
import { useLocation, useNavigate } from 'react-router-dom';
import { useErisAuthStore } from 'src/api/eris/store/ErisAuthStore';
import { useErisUserStore } from 'src/api/eris/store/ErisUserStore';
import {
  createAuthConfig,
  getAuthRedirectPathname,
  getAuthorizeUrlPreview,
  getErisLoginPath,
  getMissingAuthConfigKeys,
} from './authConfig';
import {
  clearAuthAccessToken,
  fetchCurrentUser,
  postBackendLogout,
  setAuthAccessToken,
} from './authApi';
import { AppAuthContext, type AuthContextValue, type AuthStatus } from './useAuth';

type AuthProviderProps = {
  children: React.ReactNode;
};

type AuthBridgeProps = AuthProviderProps & {
  tokenExpired: boolean;
  onTokenExpiredHandled: () => void;
};

function DisabledAuthProvider({ children }: AuthProviderProps) {
  const missingKeys = getMissingAuthConfigKeys();
  const value = useMemo<AuthContextValue>(
    () => ({
      clearError: () => undefined,
      error: missingKeys.length > 0 ? `Missing OAuth config: ${missingKeys.join(', ')}` : null,
      isAuthenticated: false,
      isConfigured: missingKeys.length === 0,
      loadUser: async () => null,
      login: () => undefined,
      logout: async () => undefined,
      status: 'unauthenticated',
      token: null,
      user: null,
    }),
    [missingKeys]
  );

  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
}

function AuthBridge({ children, onTokenExpiredHandled, tokenExpired }: AuthBridgeProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { setAuthenticated } = useErisAuthStore();
  const { user, setUser } = useErisUserStore();
  const { token, logIn, logOut, error, loginInProgress } = useOAuthAuthContext();
  const [authError, setAuthError] = useState<string | null>(null);
  const [statusOverride, setStatusOverride] = useState<AuthStatus | null>(null);
  const [userLoading, setUserLoading] = useState(false);
  const lastLoadedTokenRef = useRef<string | null>(null);
  const callbackPath = getAuthRedirectPathname();
  const loginPath = getErisLoginPath();

  const resetState = useCallback(() => {
    clearAuthAccessToken();
    lastLoadedTokenRef.current = null;
    setUserLoading(false);
    startTransition(() => {
      setAuthenticated(false);
      setUser(null);
    });
  }, [setAuthenticated, setUser]);

  const loadUser = useCallback(async () => {
    if (!token) {
      resetState();
      return null;
    }

    const nextUser = await fetchCurrentUser(token);
    startTransition(() => {
      setUser(nextUser);
      setAuthenticated(nextUser !== null);
    });
    lastLoadedTokenRef.current = token;
    return nextUser;
  }, [resetState, setAuthenticated, setUser, token]);

  const clearError = useCallback(() => {
    setAuthError(null);
    setStatusOverride(null);
    onTokenExpiredHandled();
  }, [onTokenExpiredHandled]);

  const handleFetchFailure = useCallback(
    (message: string) => {
      setAuthError(message);
      setStatusOverride('unauthenticated');
      logOut();
      resetState();
      navigate(loginPath, { replace: true });
    },
    [logOut, loginPath, navigate, resetState]
  );

  const login = useCallback(() => {
    clearError();
    if (process.env.NODE_ENV !== 'production') {
      const authorizeUrlPreview = getAuthorizeUrlPreview(location.pathname);
      if (authorizeUrlPreview) {
        console.info(
          'OAuth authorize URL preview. The actual code_challenge is generated by react-oauth2-code-pkce at redirect time.',
          authorizeUrlPreview
        );
      }
    }
    logIn(location.pathname, undefined, 'redirect');
  }, [clearError, location.pathname, logIn]);

  const logout = useCallback(async () => {
    clearError();

    try {
      await postBackendLogout();
    } catch {
      // Backend logout is optional for OAuth frontend state, so we keep logout resilient.
    } finally {
      logOut();
      resetState();
    }
  }, [clearError, logOut, resetState]);

  useEffect(() => {
    setAuthAccessToken(token || null);
    if (!token) {
      setUserLoading(false);
      resetState();
    }
  }, [resetState, token]);

  useEffect(() => {
    if (!error) return;

    if (/failed to fetch/i.test(error)) {
      handleFetchFailure(error);
      return;
    }

    setAuthError(error);
    if (loginInProgress || location.pathname === callbackPath) {
      setStatusOverride('callback_error');
      return;
    }

    setStatusOverride('unauthenticated');
  }, [callbackPath, error, handleFetchFailure, location.pathname, loginInProgress]);

  useEffect(() => {
    if (!tokenExpired) return;

    setAuthError('OAuth session expired. Please log in again.');
    setStatusOverride('token_expired');
    logOut();
    resetState();
    onTokenExpiredHandled();
  }, [logOut, onTokenExpiredHandled, resetState, tokenExpired]);

  useEffect(() => {
    if (!token) {
      setUserLoading(false);
      return;
    }
    if (lastLoadedTokenRef.current === token && user) return;

    let cancelled = false;
    setUserLoading(true);

    loadUser()
      .then(() => {
        if (!cancelled) setStatusOverride(null);
      })
      .catch((loadUserError: unknown) => {
        if (cancelled) return;

        const message =
          loadUserError instanceof Error
            ? loadUserError.message
            : 'Failed to bootstrap the authenticated user.';

        if (/failed to fetch/i.test(message)) {
          handleFetchFailure(message);
          return;
        }

        setAuthError(message);
        setStatusOverride('token_expired');
        logOut();
        resetState();
      })
      .finally(() => {
        if (!cancelled) setUserLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [handleFetchFailure, loadUser, logOut, resetState, token, user]);

  const status = useMemo<AuthStatus>(() => {
    if (loginInProgress || userLoading) return 'loading';
    if (statusOverride) return statusOverride;
    if (token && !user) return 'loading';
    if (token && user) return 'authenticated';
    return 'unauthenticated';
  }, [loginInProgress, statusOverride, token, user, userLoading]);

  const value = useMemo<AuthContextValue>(
    () => ({
      clearError,
      error: authError,
      isAuthenticated: status === 'authenticated',
      isConfigured: true,
      loadUser,
      login,
      logout,
      status,
      token: token || null,
      user,
    }),
    [authError, clearError, loadUser, login, logout, status, token, user]
  );

  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
}

export function AppAuthProvider({ children }: AuthProviderProps) {
  const [tokenExpired, setTokenExpired] = useState(false);
  const authConfig = useMemo(
    () =>
      createAuthConfig({
        onRefreshTokenExpire: () => {
          setTokenExpired(true);
        },
      }),
    []
  );

  if (!authConfig) {
    return <DisabledAuthProvider>{children}</DisabledAuthProvider>;
  }

  return (
    <OAuthAuthProvider authConfig={authConfig}>
      <AuthBridge tokenExpired={tokenExpired} onTokenExpiredHandled={() => setTokenExpired(false)}>
        {children}
      </AuthBridge>
    </OAuthAuthProvider>
  );
}
